import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Database, RefreshCw, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Repository() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'backfill' | 'sync-all'

  const isAdmin = user?.role === "admin";

  const load = () => {
    if (!isAdmin) return;
    setLoading(true);
    api.get("/admin/repository/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const backfill = async () => {
    setBusy("backfill");
    try {
      const r = await api.post("/admin/repository/backfill", null, { timeout: 180000 });
      toast.success(`Repo backfilled: ${r.data.processed.toLocaleString()} rows merged · ${r.data.repo_total.toLocaleString()} total`);
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(null); }
  };

  const syncAll = async () => {
    setBusy("sync-all");
    try {
      const r = await api.post("/admin/repository/sync-all?weeks=4", null, { timeout: 180000 });
      const total = (r.data.results || []).reduce((s, x) => s + (x.terms_inserted || 0), 0);
      toast.success(`Sync complete — ${total.toLocaleString()} terms materialized across ${r.data.results.length} brands`);
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(null); }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-900">
        <div className="font-semibold text-lg flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Admins only</div>
        <p className="mt-1 text-sm">The shared keyword repository is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="repository-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 inline-flex items-center gap-1">
            <Database className="h-3 w-3" /> Shared Repository
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Global keyword pool</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every search-term upload from any brand is merged into this de-duplicated pool, indexed by (marketplace, country, week, term). New brands automatically pull the last 4 weeks of category-matched data on creation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="repo-refresh" onClick={load} variant="outline" className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
          <Button data-testid="repo-backfill" onClick={backfill} disabled={!!busy} variant="outline" className="gap-1.5">
            {busy === "backfill" ? "Backfilling…" : <><Sparkles className="h-3.5 w-3.5" /> Backfill from existing</>}
          </Button>
          <Button data-testid="repo-sync-all" onClick={syncAll} disabled={!!busy} className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5">
            {busy === "sync-all" ? "Syncing…" : "Sync all brands"}
          </Button>
        </div>
      </div>

      {loading && <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">Loading…</div>}

      {!!stats && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card label="Total rows" value={stats.total_rows.toLocaleString()} sub="unique (marketplace, country, week, term)" />
            <Card label="Weeks indexed" value={stats.weeks.length} sub="distinct weekly snapshots" />
            <Card label="Categories detected" value={stats.categories.length} sub="from cluster classification" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="text-base font-semibold text-slate-900">Recent weekly snapshots</div>
                <div className="text-xs text-slate-500">Most recent 100 weeks across marketplaces</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 text-left">Week</th>
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-4 py-2 text-right">Rows</th>
                    <th className="px-4 py-2 text-right">Brands</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.weeks.map((w, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{w.week_start_date}</td>
                      <td className="px-4 py-2 text-slate-600">{w.marketplace} · {w.country}</td>
                      <td className="px-4 py-2 text-right font-mono">{w.row_count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-mono">{w.contributor_count}</td>
                    </tr>
                  ))}
                  {!stats.weeks.length && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-xs text-slate-500">Repo is empty — click "Backfill from existing" to seed it.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-5 py-3 border-b border-slate-100">
                <div className="text-base font-semibold text-slate-900">Top categories in pool</div>
                <div className="text-xs text-slate-500">From auto-detected cluster categories</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 text-left">Category</th>
                    <th className="px-4 py-2 text-right">Rows</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.categories.map((c, i) => {
                    const pct = stats.total_rows ? Math.round((c.rows / stats.total_rows) * 100) : 0;
                    return (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="px-4 py-2 font-medium text-slate-800">{c.category}</td>
                        <td className="px-4 py-2 text-right font-mono">{c.rows.toLocaleString()}</td>
                        <td className="px-4 py-2 w-32">
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!stats.categories.length && (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-xs text-slate-500">No categories detected yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
